/**
 * Default Budget Category Definitions
 * Predefined categories for PlanLens Budgeting feature
 */

/**
 * Get all default budget categories organized by group
 * @returns {Array} Array of category objects
 */
export const getDefaultCategories = () => {
  return [
    // ===== 1. INCOME =====
    {
      id: 'primary-salary',
      name: 'Primary Salary',
      group: 'Income',
      type: 'income',
      isDefault: true,
      sortOrder: 1
    },
    {
      id: 'secondary-salary',
      name: 'Secondary Salary',
      group: 'Income',
      type: 'income',
      isDefault: true,
      sortOrder: 2
    },
    {
      id: 'bonus',
      name: 'Bonus',
      group: 'Income',
      type: 'income',
      isDefault: true,
      sortOrder: 3
    },
    {
      id: 'overtime',
      name: 'Overtime',
      group: 'Income',
      type: 'income',
      isDefault: true,
      sortOrder: 4
    },
    {
      id: 'freelance-income',
      name: 'Freelance / Contract Income',
      group: 'Income',
      type: 'income',
      isDefault: true,
      sortOrder: 5
    },
    {
      id: 'rental-income',
      name: 'Rental Income',
      group: 'Income',
      type: 'income',
      isDefault: true,
      sortOrder: 6
    },
    {
      id: 'benefits',
      name: 'Benefits',
      group: 'Income',
      type: 'income',
      isDefault: true,
      sortOrder: 7
    },
    {
      id: 'other-income',
      name: 'Other Income',
      group: 'Income',
      type: 'income',
      isDefault: true,
      sortOrder: 8
    },

    // ===== 2. HOUSEHOLD BILLS =====
    // Housing
    {
      id: 'mortgage-payment',
      name: 'Mortgage Payment',
      group: 'Household Bills',
      subgroup: 'Housing',
      type: 'expense',
      isDefault: true,
      sortOrder: 10
    },
    {
      id: 'rent',
      name: 'Rent',
      group: 'Household Bills',
      subgroup: 'Housing',
      type: 'expense',
      isDefault: true,
      sortOrder: 11
    },
    {
      id: 'mortgage-endowment',
      name: 'Mortgage Endowment',
      group: 'Household Bills',
      subgroup: 'Housing',
      type: 'expense',
      isDefault: true,
      sortOrder: 12
    },
    {
      id: 'mortgage-life-insurance',
      name: 'Mortgage Life Insurance',
      group: 'Household Bills',
      subgroup: 'Housing',
      type: 'expense',
      isDefault: true,
      sortOrder: 13
    },
    {
      id: 'ground-rent',
      name: 'Ground Rent',
      group: 'Household Bills',
      subgroup: 'Property Charges',
      type: 'expense',
      isDefault: true,
      sortOrder: 14
    },
    {
      id: 'service-charge',
      name: 'Service Charge',
      group: 'Household Bills',
      subgroup: 'Property Charges',
      type: 'expense',
      isDefault: true,
      sortOrder: 15
    },

    // Utilities
    {
      id: 'council-tax',
      name: 'Council Tax / Rates',
      group: 'Household Bills',
      subgroup: 'Utilities',
      type: 'expense',
      isDefault: true,
      sortOrder: 20
    },
    {
      id: 'gas',
      name: 'Gas',
      group: 'Household Bills',
      subgroup: 'Utilities',
      type: 'expense',
      isDefault: true,
      sortOrder: 21
    },
    {
      id: 'electricity',
      name: 'Electricity',
      group: 'Household Bills',
      subgroup: 'Utilities',
      type: 'expense',
      isDefault: true,
      sortOrder: 22
    },
    {
      id: 'water',
      name: 'Water',
      group: 'Household Bills',
      subgroup: 'Utilities',
      type: 'expense',
      isDefault: true,
      sortOrder: 23
    },
    {
      id: 'tv-licence',
      name: 'TV Licence',
      group: 'Household Bills',
      subgroup: 'Utilities',
      type: 'expense',
      isDefault: true,
      sortOrder: 24
    },

    // Communications
    {
      id: 'internet',
      name: 'Internet / Broadband',
      group: 'Household Bills',
      subgroup: 'Communications',
      type: 'expense',
      isDefault: true,
      sortOrder: 30
    },
    {
      id: 'landline',
      name: 'Landline',
      group: 'Household Bills',
      subgroup: 'Communications',
      type: 'expense',
      isDefault: true,
      sortOrder: 31
    },
    {
      id: 'mobile-phone',
      name: 'Mobile Phone',
      group: 'Household Bills',
      subgroup: 'Communications',
      type: 'expense',
      isDefault: true,
      sortOrder: 32
    },

    // ===== 3. LIVING COSTS =====
    {
      id: 'groceries',
      name: 'Groceries',
      group: 'Living Costs',
      type: 'expense',
      isDefault: true,
      sortOrder: 40
    },
    {
      id: 'household-shopping',
      name: 'Household Shopping',
      group: 'Living Costs',
      type: 'expense',
      isDefault: true,
      sortOrder: 41
    },
    {
      id: 'public-transport',
      name: 'Public Transport',
      group: 'Living Costs',
      type: 'expense',
      isDefault: true,
      sortOrder: 42
    },
    {
      id: 'fuel',
      name: 'Fuel',
      group: 'Living Costs',
      type: 'expense',
      isDefault: true,
      sortOrder: 43
    },
    {
      id: 'parking',
      name: 'Parking',
      group: 'Living Costs',
      type: 'expense',
      isDefault: true,
      sortOrder: 44
    },
    {
      id: 'taxis',
      name: 'Taxis / Ride-hailing',
      group: 'Living Costs',
      type: 'expense',
      isDefault: true,
      sortOrder: 45
    },
    {
      id: 'clothing',
      name: 'Clothing',
      group: 'Living Costs',
      type: 'expense',
      isDefault: true,
      sortOrder: 46
    },
    {
      id: 'haircuts',
      name: 'Haircuts',
      group: 'Living Costs',
      type: 'expense',
      isDefault: true,
      sortOrder: 47
    },
    {
      id: 'personal-care',
      name: 'Personal Care',
      group: 'Living Costs',
      type: 'expense',
      isDefault: true,
      sortOrder: 48
    },

    // ===== 4. FAMILY =====
    {
      id: 'childcare',
      name: 'Childcare',
      group: 'Family',
      type: 'expense',
      isDefault: true,
      sortOrder: 50
    },
    {
      id: 'school-fees',
      name: 'School Fees',
      group: 'Family',
      type: 'expense',
      isDefault: true,
      sortOrder: 51
    },
    {
      id: 'school-meals',
      name: 'School Meals',
      group: 'Family',
      type: 'expense',
      isDefault: true,
      sortOrder: 52
    },
    {
      id: 'clubs-activities',
      name: 'Clubs & Activities',
      group: 'Family',
      type: 'expense',
      isDefault: true,
      sortOrder: 53
    },
    {
      id: 'child-maintenance',
      name: 'Child Maintenance',
      group: 'Family',
      type: 'expense',
      isDefault: true,
      sortOrder: 54
    },
    {
      id: 'family-support',
      name: 'Family Support',
      group: 'Family',
      type: 'expense',
      isDefault: true,
      sortOrder: 55
    },

    // ===== 5. WORK, TRAVEL & CAR =====
    // Car Ownership
    {
      id: 'car-finance',
      name: 'Car Finance / Hire Purchase',
      group: 'Work, Travel & Car',
      subgroup: 'Car Ownership',
      type: 'expense',
      isDefault: true,
      sortOrder: 60
    },
    {
      id: 'car-insurance',
      name: 'Car Insurance',
      group: 'Work, Travel & Car',
      subgroup: 'Car Ownership',
      type: 'expense',
      isDefault: true,
      sortOrder: 61
    },
    {
      id: 'road-tax',
      name: 'Road Tax',
      group: 'Work, Travel & Car',
      subgroup: 'Car Ownership',
      type: 'expense',
      isDefault: true,
      sortOrder: 62
    },
    {
      id: 'mot-servicing',
      name: 'MOT & Servicing',
      group: 'Work, Travel & Car',
      subgroup: 'Car Ownership',
      type: 'expense',
      isDefault: true,
      sortOrder: 63
    },
    {
      id: 'breakdown-cover',
      name: 'Breakdown Cover',
      group: 'Work, Travel & Car',
      subgroup: 'Car Ownership',
      type: 'expense',
      isDefault: true,
      sortOrder: 64
    },

    // Work & Travel
    {
      id: 'work-travel',
      name: 'Work Travel',
      group: 'Work, Travel & Car',
      subgroup: 'Work & Travel',
      type: 'expense',
      isDefault: true,
      sortOrder: 70
    },
    {
      id: 'business-expenses',
      name: 'Business Expenses (Non-reimbursed)',
      group: 'Work, Travel & Car',
      subgroup: 'Work & Travel',
      type: 'expense',
      isDefault: true,
      sortOrder: 71
    },
    {
      id: 'professional-fees',
      name: 'Professional Fees',
      group: 'Work, Travel & Car',
      subgroup: 'Work & Travel',
      type: 'expense',
      isDefault: true,
      sortOrder: 72
    },
    {
      id: 'training-courses',
      name: 'Training & Courses',
      group: 'Work, Travel & Car',
      subgroup: 'Work & Travel',
      type: 'expense',
      isDefault: true,
      sortOrder: 73
    },

    // ===== 6. FINANCE & INSURANCE =====
    // Insurance
    {
      id: 'life-insurance',
      name: 'Life Insurance',
      group: 'Finance & Insurance',
      subgroup: 'Insurance',
      type: 'expense',
      isDefault: true,
      sortOrder: 80
    },
    {
      id: 'income-protection',
      name: 'Income Protection Insurance',
      group: 'Finance & Insurance',
      subgroup: 'Insurance',
      type: 'expense',
      isDefault: true,
      sortOrder: 81
    },
    {
      id: 'critical-illness',
      name: 'Critical Illness Insurance',
      group: 'Finance & Insurance',
      subgroup: 'Insurance',
      type: 'expense',
      isDefault: true,
      sortOrder: 82
    },
    {
      id: 'health-insurance',
      name: 'Health Insurance',
      group: 'Finance & Insurance',
      subgroup: 'Insurance',
      type: 'expense',
      isDefault: true,
      sortOrder: 83
    },
    {
      id: 'dental-insurance',
      name: 'Dental Insurance',
      group: 'Finance & Insurance',
      subgroup: 'Insurance',
      type: 'expense',
      isDefault: true,
      sortOrder: 84
    },

    // Banking Fees
    {
      id: 'overdraft-charges',
      name: 'Overdraft Charges & Interest',
      group: 'Finance & Insurance',
      subgroup: 'Banking Fees & Charges',
      type: 'expense',
      isDefault: true,
      sortOrder: 90
    },
    {
      id: 'bank-fees',
      name: 'Bank Account Fees',
      group: 'Finance & Insurance',
      subgroup: 'Banking Fees & Charges',
      type: 'expense',
      isDefault: true,
      sortOrder: 91
    },
    {
      id: 'penalties-charges',
      name: 'Penalties & Charges',
      group: 'Finance & Insurance',
      subgroup: 'Banking Fees & Charges',
      type: 'expense',
      isDefault: true,
      sortOrder: 92
    },

    // Loans & Credit
    {
      id: 'loan-repayments',
      name: 'Loan Repayments',
      group: 'Finance & Insurance',
      subgroup: 'Loans & Credit',
      type: 'expense',
      isDefault: true,
      sortOrder: 100
    },
    {
      id: 'student-loan',
      name: 'Student Loan Repayments',
      group: 'Finance & Insurance',
      subgroup: 'Loans & Credit',
      type: 'expense',
      isDefault: true,
      sortOrder: 101
    },
    {
      id: 'credit-card',
      name: 'Credit Card Repayments',
      group: 'Finance & Insurance',
      subgroup: 'Loans & Credit',
      type: 'expense',
      isDefault: true,
      sortOrder: 102
    },
    {
      id: 'bnpl',
      name: 'Buy Now, Pay Later',
      group: 'Finance & Insurance',
      subgroup: 'Loans & Credit',
      type: 'expense',
      isDefault: true,
      sortOrder: 103
    },
    {
      id: 'other-credit',
      name: 'Other Credit Repayments',
      group: 'Finance & Insurance',
      subgroup: 'Loans & Credit',
      type: 'expense',
      isDefault: true,
      sortOrder: 104
    },

    // Savings & Investments (type: savings)
    {
      id: 'regular-savings',
      name: 'Regular Savings',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 110
    },
    {
      id: 'lump-sum-savings',
      name: 'Lump Sum Savings',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 111
    },
    {
      id: 'cash-savings',
      name: 'Cash Savings',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 112
    },
    {
      id: 'emergency-fund',
      name: 'Emergency Fund',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 113
    },
    {
      id: 'stocks-isa',
      name: 'Stocks & Shares ISA Contributions',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 114
    },
    {
      id: 'general-investment',
      name: 'General Investment Account Contributions',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 115
    },
    {
      id: 'buying-shares',
      name: 'Buying Shares & Other Investments',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 116
    },
    {
      id: 'crypto-investments',
      name: 'Crypto Investments',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 117
    },
    {
      id: 'private-pension',
      name: 'Private Pension Contributions',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 118
    },
    {
      id: 'sipp',
      name: 'SIPP Contributions',
      group: 'Finance & Insurance',
      subgroup: 'Savings & Investments',
      type: 'savings',
      isDefault: true,
      sortOrder: 119
    },

    // Future Financial Plans
    {
      id: 'long-term-care',
      name: 'Long-Term Care Plan',
      group: 'Finance & Insurance',
      subgroup: 'Future Financial Plans',
      type: 'expense',
      isDefault: true,
      sortOrder: 120
    },
    {
      id: 'funeral-plan',
      name: 'Funeral Plan',
      group: 'Finance & Insurance',
      subgroup: 'Future Financial Plans',
      type: 'expense',
      isDefault: true,
      sortOrder: 121
    },

    // Financial & Legal Advice
    {
      id: 'financial-advice',
      name: 'Financial Advice',
      group: 'Finance & Insurance',
      subgroup: 'Financial & Legal Advice',
      type: 'expense',
      isDefault: true,
      sortOrder: 130
    },
    {
      id: 'legal-advice',
      name: 'Legal Advice',
      group: 'Finance & Insurance',
      subgroup: 'Financial & Legal Advice',
      type: 'expense',
      isDefault: true,
      sortOrder: 131
    },

    // ===== 7. LEISURE & HOLIDAYS =====
    // Subscriptions
    {
      id: 'streaming-services',
      name: 'Streaming Services',
      group: 'Leisure & Holidays',
      subgroup: 'Subscriptions',
      type: 'expense',
      isDefault: true,
      sortOrder: 140
    },
    {
      id: 'music-subscriptions',
      name: 'Music Subscriptions',
      group: 'Leisure & Holidays',
      subgroup: 'Subscriptions',
      type: 'expense',
      isDefault: true,
      sortOrder: 141
    },
    {
      id: 'software-subscriptions',
      name: 'Software Subscriptions',
      group: 'Leisure & Holidays',
      subgroup: 'Subscriptions',
      type: 'expense',
      isDefault: true,
      sortOrder: 142
    },
    {
      id: 'news-media',
      name: 'News / Media',
      group: 'Leisure & Holidays',
      subgroup: 'Subscriptions',
      type: 'expense',
      isDefault: true,
      sortOrder: 143
    },

    // Eating & Social
    {
      id: 'eating-out',
      name: 'Eating Out',
      group: 'Leisure & Holidays',
      subgroup: 'Eating & Social',
      type: 'expense',
      isDefault: true,
      sortOrder: 150
    },
    {
      id: 'takeaways',
      name: 'Takeaways',
      group: 'Leisure & Holidays',
      subgroup: 'Eating & Social',
      type: 'expense',
      isDefault: true,
      sortOrder: 151
    },
    {
      id: 'socialising',
      name: 'Socialising',
      group: 'Leisure & Holidays',
      subgroup: 'Eating & Social',
      type: 'expense',
      isDefault: true,
      sortOrder: 152
    },

    // Leisure
    {
      id: 'gym-fitness',
      name: 'Gym / Fitness',
      group: 'Leisure & Holidays',
      subgroup: 'Leisure',
      type: 'expense',
      isDefault: true,
      sortOrder: 160
    },
    {
      id: 'hobbies',
      name: 'Hobbies',
      group: 'Leisure & Holidays',
      subgroup: 'Leisure',
      type: 'expense',
      isDefault: true,
      sortOrder: 161
    },
    {
      id: 'entertainment',
      name: 'Entertainment',
      group: 'Leisure & Holidays',
      subgroup: 'Leisure',
      type: 'expense',
      isDefault: true,
      sortOrder: 162
    },

    // Holidays
    {
      id: 'flights',
      name: 'Flights',
      group: 'Leisure & Holidays',
      subgroup: 'Holidays',
      type: 'expense',
      isDefault: true,
      sortOrder: 170
    },
    {
      id: 'accommodation',
      name: 'Accommodation',
      group: 'Leisure & Holidays',
      subgroup: 'Holidays',
      type: 'expense',
      isDefault: true,
      sortOrder: 171
    },
    {
      id: 'holiday-spending',
      name: 'Holiday Spending',
      group: 'Leisure & Holidays',
      subgroup: 'Holidays',
      type: 'expense',
      isDefault: true,
      sortOrder: 172
    },

    // ===== 8. ONE-OFF / IRREGULAR =====
    {
      id: 'home-repairs',
      name: 'Home Repairs',
      group: 'One-off / Irregular',
      type: 'expense',
      isDefault: true,
      sortOrder: 180
    },
    {
      id: 'furniture',
      name: 'Furniture',
      group: 'One-off / Irregular',
      type: 'expense',
      isDefault: true,
      sortOrder: 181
    },
    {
      id: 'appliances',
      name: 'Appliances',
      group: 'One-off / Irregular',
      type: 'expense',
      isDefault: true,
      sortOrder: 182
    },
    {
      id: 'medical-costs',
      name: 'Medical Costs',
      group: 'One-off / Irregular',
      type: 'expense',
      isDefault: true,
      sortOrder: 183
    },
    {
      id: 'legal-oneoff-fees',
      name: 'Legal One-off Fees',
      group: 'One-off / Irregular',
      type: 'expense',
      isDefault: true,
      sortOrder: 184
    },

    // Uncategorized fallback
    {
      id: 'uncategorized',
      name: 'Uncategorized',
      group: 'Other',
      type: 'expense',
      isDefault: true,
      sortOrder: 999
    }
  ];
};

/**
 * Get categories grouped by their group name
 * @returns {Object} Object with group names as keys
 */
export const getCategoriesByGroup = () => {
  const categories = getDefaultCategories();
  const grouped = {};

  categories.forEach(cat => {
    if (!grouped[cat.group]) {
      grouped[cat.group] = [];
    }
    grouped[cat.group].push(cat);
  });

  return grouped;
};

/**
 * Get categories by type
 * @param {string} type - income|expense|savings
 * @returns {Array} Filtered categories
 */
export const getCategoriesByType = (type) => {
  return getDefaultCategories().filter(cat => cat.type === type);
};

/**
 * Get category by ID
 * @param {string} id - Category ID
 * @returns {Object|null} Category object or null
 */
export const getCategoryById = (id) => {
  return getDefaultCategories().find(cat => cat.id === id) || null;
};

/**
 * Get all group names
 * @returns {Array} Array of unique group names
 */
export const getGroupNames = () => {
  const categories = getDefaultCategories();
  const groups = [...new Set(categories.map(cat => cat.group))];
  return groups.filter(g => g !== 'Other'); // Sort Other to end
};

/**
 * Get color for category type
 * @param {string} type - income|expense|savings
 * @returns {string} Tailwind gradient class
 */
export const getCategoryTypeColor = (type) => {
  const colors = {
    income: 'from-green-500 to-emerald-600',
    expense: 'from-red-500 to-orange-600',
    savings: 'from-blue-500 to-indigo-600'
  };
  return colors[type] || 'from-gray-500 to-gray-600';
};

/**
 * Get icon for category group
 * @param {string} group - Group name
 * @returns {string} Lucide icon name
 */
export const getGroupIcon = (group) => {
  const icons = {
    'Income': 'TrendingUp',
    'Household Bills': 'Home',
    'Living Costs': 'ShoppingBag',
    'Family': 'Users',
    'Work, Travel & Car': 'Car',
    'Finance & Insurance': 'Shield',
    'Leisure & Holidays': 'Palmtree',
    'One-off / Irregular': 'Calendar',
    'Other': 'MoreHorizontal'
  };
  return icons[group] || 'Circle';
};

/**
 * Search categories by name
 * @param {string} query - Search query
 * @returns {Array} Matching categories
 */
export const searchCategories = (query) => {
  const lowerQuery = query.toLowerCase();
  return getDefaultCategories().filter(cat =>
    cat.name.toLowerCase().includes(lowerQuery) ||
    cat.group.toLowerCase().includes(lowerQuery)
  );
};
